import React, { useMemo } from "react";
import {
  Modal,
  ModalBody,
  Card,
  CardBody,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Badge,
} from "reactstrap";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { API_BASE_URL } from "../../api/api";
import { useRanks } from "../../Hooks/use-ranks";

const AVATAR_COLORS = [
  "#d1fae5",
  "#dbeafe",
  "#fef3c7",
  "#ede9fe",
  "#fee2e2",
  "#e0f2fe",
  "#fce7f3",
];

const getRankImage = (rankName) => {
  const name = rankName
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

  switch (name) {
    case "aprendiz elite":
      return "/images/rank/01.png";
    case "constructor":
      return "/images/rank/02.png";
    case "master":
    case "máster":
      return "/images/rank/03.png";
    case "esmeralda":
      return "/images/rank/04.png";
    case "diamante":
      return "/images/rank/05.png";
    case "diamante embajador":
      return "/images/rank/06.png";
    case "diamante embajador estrella":
    case "diamante embajador estrella 🌟":
      return "/images/rank/07.png";
    default:
      return "/images/rank/01.png";
  }
};

const getRankColor = (rankName) => {
  const name = rankName
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  switch (name) {
    case "master":
      return "#FFD700";
    case "esmeralda":
      return "#008000";
    case "constructor":
      return "#0000FF";
    case "diamante":
      return "#C0C0C0";
    case "aprendiz elite":
      return "#800080";
    case "diamante embajador":
      return "#00CED1";
    case "diamante embajador estrella":
    case "diamante embajador estrella 🌟":
      return "#FF1493";
    default:
      return "#8B949E";
  }
};

const RankBadge = ({ rank }) => {
  const rankConfig = {
    "Platino": { icon: "💎", color: "#8b5cf6" },
    "Oro": { icon: "👑", color: "#f59e0b" },
    "Plata": { icon: "🥈", color: "#9ca3af" },
    "Bronce": { icon: "🥉", color: "#d97706" },
  };
  const config = rankConfig[rank] || { icon: "⭐", color: "#6b7280" };

  const rankImage = getRankImage(rank);
  const isSystemRank = rankImage !== "/images/rank/01.png" || 
    (rank && !["Platino", "Oro", "Plata", "Bronce"].includes(rank));

  if (isSystemRank) {
    return (
      <div className="d-flex align-items-center gap-2">
        <img
          src={rankImage}
          alt={rank || "Sin rango"}
          style={{
            width: "24px",
            height: "24px",
            objectFit: "contain",
          }}
        />
        <span className="fw-bold" style={{ color: getRankColor(rank) }}>
          {rank || "Sin rango"}
        </span>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center gap-2">
      <span style={{ fontSize: "18px" }}>{config.icon}</span>
      <span className="fw-bold" style={{ color: config.color }}>
        {rank || "Sin rango"}
      </span>
    </div>
  );
};

const getInitials = (name) => {
  if (!name) return "??";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
};

const getColor = (seed = "") => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash + seed.charCodeAt(i)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[hash];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const formatPoints = (value) => (value || 0).toLocaleString("es-CO");

const resolvePoints = (person) => {
  const rankPoints = person?.last_rank?.total_points;
  if (typeof rankPoints === "number" && rankPoints > 0) return rankPoints;
  return (person?.loyalty_points || 0) + (person?.influencer_points || 0);
};

const UserRedAscendenteModal = ({ isOpen, toggle, user }) => {
  const { getData } = useDataFetch();

  const userId = user?.id || user?._id;

  const { data, isLoading, error } = getData(
    ["network-upline", userId],
    "/users/admin/network/upline",
    { enabled: Boolean(isOpen && userId) },
    { user_id: userId }
  );

   const {
      currentUserPoints,
    } = useRanks(user?.id);

  const { root, uplines } = useMemo(() => {
    const payload = data?.data;
    if (!payload) return { root: null, uplines: [] };

    return {
      root: payload.root,
      uplines: (payload.users || []).map((ancestor) => ({
        ...ancestor,
        initials: getInitials(ancestor.name),
        color: getColor(ancestor.multyhealth_code || ancestor.id),
        points: resolvePoints(ancestor),
        rank: ancestor.last_rank?.rank?.name,
      })),
    };
  }, [data]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable style={{ maxWidth: "1200px" }}>
      <ModalBody className="p-0">
        <div className="p-4 pb-0">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
            <div>
              <h2 className="fw-bold mb-1">RED ASCENDENTE</h2>
              <p className="text-muted mb-0 small">
                Visualización de uplines hasta 7 niveles (navegación hacia arriba)
              </p>
            </div>
            <Button 
              color="light" 
              onClick={toggle}
              className="rounded-circle"
              style={{ 
                width: "36px", 
                height: "36px",
                padding: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #dee2e6"
              }}
            >
              ✕
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 small text-muted mb-0">Cargando red ascendente...</p>
          </div>
        )}

        {error && (
          <div className="px-4 pb-4">
            <Alert color="danger" className="mb-0">
              <i className="fa fa-exclamation-triangle me-2" />
              {error.message}
            </Alert>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {root && (
              <div className="px-4">
                <Card className="mb-4" style={{ border: "1px solid #e9ecef", borderRadius: "12px" }}>
                  <CardBody>
                    <Row>
                      <Col md="4" className="d-flex align-items-center">
                        <div
                          className="me-3"
                          style={{
                            width: 80,
                            height: 80,
                          }}
                        >
                          <img
                            src={`${API_BASE_URL}/users/show/photo/${root?.photo || ""}`}
                            alt={root?.name || "Usuario"}
                            style={{
                              width: "80px",
                              height: "80px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "2px solid #e9ecef",
                            }}
                          />
                        </div>
                        <div className="ms-3">
                          <h5 className="fw-bold mb-0">{root?.name || "-"}</h5>
                          <small className="text-muted">Código: {root?.multyhealth_code || "-"}</small>
                          <div>
                            <Badge color="success" className="mt-1">
                              <i className="fa fa-circle me-1" style={{ fontSize: "6px" }} /> Activo
                            </Badge>
                          </div>
                        </div>
                      </Col>

                      <Col md="3" style={{ borderLeft: "1px solid #e9ecef", paddingLeft: "20px" }}>
                        <p className="text-muted small mb-0">Rango Actual</p>
                        <div className="d-flex align-items-center gap-2">
                          <h3 className="fw-bold mb-0" style={{ color: getRankColor(root?.last_rank?.rank?.name) }}>
                            {root?.last_rank?.rank?.name || "Sin rango"}
                          </h3>
                          <img
                            src={getRankImage(root?.last_rank?.rank?.name)}
                            alt={root?.last_rank?.rank?.name || "Sin rango"}
                            style={{
                              width: 30,
                              height: 30,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        <h5 className="text-muted">{currentUserPoints.toLocaleString()}</h5>
                      </Col>

                      <Col md="2" style={{ borderLeft: "1px solid #e9ecef", paddingLeft: "20px" }}>
                        <p className="text-muted small mb-0">Total de Directos</p>
                        <h3 className="fw-bold mb-0">{formatPoints(root.total_directs)}</h3>
                      </Col>

                      <Col md="3" style={{ borderLeft: "1px solid #e9ecef", paddingLeft: "20px" }}>
                        <p className="text-muted small mb-0">Total de Red</p>
                        <h3 className="fw-bold mb-0">{formatPoints(root.total_registered_users)}</h3>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </div>
            )}

            <div className="p-4">
              <Card className="shadow-sm border-0">
                <CardBody className="p-0">
                  <div className="p-4 pb-2">
                    <h5 className="fw-bold mb-0">
                      Uplines (Red Ascendente hasta 7 niveles)
                    </h5>
                  </div>
                  <div className="table-responsive">
                    <Table hover className="align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="ps-4" style={{ width: "80px" }}>Nivel</th>
                          <th>Código</th>
                          <th>Nombre de Usuario</th>
                          <th>Rango</th>
                          <th>Puntos</th>
                          <th className="pe-4">Fecha de Afiliación</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uplines.map((item) => (
                          <tr key={item.level}>
                            <td className="ps-4 fw-bold">{item.level}</td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <div
                                  className="d-flex align-items-center justify-content-center rounded-circle"
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    background: item.color,
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    color: "#374151",
                                  }}
                                >
                                  {item.initials}
                                </div>
                                <span className="text-muted small">
                                  {item.multyhealth_code || "-"}
                                </span>
                              </div>
                            </td>
                            <td className="fw-bold">{item.name || "-"}</td>
                            <td><RankBadge rank={item.rank} /></td>
                            <td className="fw-bold">{formatPoints(item.points)}</td>
                            <td className="pe-4 text-muted small">
                              {formatDateTime(item.created_at)}
                            </td>
                          </tr>
                        ))}

                        {!uplines.length && (
                          <tr>
                            <td colSpan="6" className="text-center text-muted py-4">
                              Este distribuidor no tiene patrocinadores por encima:
                              está en la raíz de la red.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>

              <Alert
                color="info"
                className="d-flex align-items-center shadow-sm border-0 mt-3"
                style={{ 
                  background: "#e8f4fd", 
                  border: "1px solid #b8d4f0",
                  borderRadius: "8px",
                  padding: "12px 16px"
                }}
              >
                <div 
                  className="me-2 d-flex align-items-center justify-content-center"
                  style={{
                    width: "24px",
                    height: "24px",
                    background: "#0d6efd",
                    borderRadius: "50%",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                    flexShrink: 0
                  }}
                >
                  i
                </div>
                <div className="small" style={{ color: "#0a3d6b" }}>
                  Se muestran los uplines directos de cada nivel hasta un máximo de
                  7 niveles. El rango es el más alto alcanzado por cada usuario en
                  los cortes de rangos.
                </div>
              </Alert>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default UserRedAscendenteModal;